import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { adicionarIngrediente, custoLinha, custoPorTamanho, definirQtd, ingredientesDaFicha, qtdDe, removerIngrediente } from "../lib/calculo";

const ESTILO = {
  ingredient: { emoji: "🥬", titulo: "Ingredientes", busca: "Buscar ingrediente para adicionar...", cab: "from-emerald-50/80 to-green-50/80 dark:from-emerald-950/30 dark:to-green-950/30", icone: "bg-emerald-100 dark:bg-emerald-900/40", sub: "text-emerald-600 dark:text-emerald-400", chip: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400" },
  packaging: { emoji: "📦", titulo: "Embalagens", busca: "Buscar embalagem para adicionar...", cab: "from-amber-50/80 to-orange-50/80 dark:from-amber-950/30 dark:to-orange-950/30", icone: "bg-amber-100 dark:bg-amber-900/40", sub: "text-amber-600 dark:text-amber-400", chip: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400" },
};

/** Painel "Ingredientes" ou "Embalagens" — a MESMA cara da ficha padrao do
 *  sistema; a unica diferenca e' um campo de quantidade por TAMANHO em cada linha. */
export default function PainelItens({
  tipo, sizes, porTamanho, onChange, ingredients, porId, Combobox, getIngredientUnit, getFractionText, onCreateNew, excludeIds = [],
}) {
  const e = ESTILO[tipo] || ESTILO.ingredient;
  const ids = ingredientesDaFicha(porTamanho, tipo);
  const subtotal = custoPorTamanho(porTamanho, sizes, porId, tipo);
  const testId = tipo === "packaging" ? "ficha-embalagens" : "ficha-ingredientes";

  return (
    <div className="bg-card rounded-2xl border shadow-sm flex flex-col" data-e2e={testId}>
      <div className={`px-4 py-3 border-b bg-gradient-to-r ${e.cab} flex items-center justify-between rounded-t-2xl`}>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg ${e.icone} flex items-center justify-center`}><span className="text-base">{e.emoji}</span></div>
          <div>
            <h3 className="text-sm font-semibold">{e.titulo}</h3>
            <p className="text-[10px] text-muted-foreground">{ids.length} item(ns)</p>
          </div>
        </div>
        {ids.length > 0 && (
          <div className="text-right">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Subtotal</p>
            <div className="flex flex-wrap justify-end gap-x-3">
              {sizes.map((sz) => (
                <p key={sz.id} className={`text-sm font-bold tabular-nums ${e.sub}`}>
                  <span className="text-[10px] text-muted-foreground font-medium uppercase mr-1">{sz.name}</span>R$ {(subtotal[sz.id] || 0).toFixed(2).replace(".", ",")}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="p-3 flex-1">
        <div className="mb-3">
          <Combobox ingredients={ingredients} value="" placeholder={e.busca}
            onChange={(id) => { if (id && !ids.includes(id)) onChange(adicionarIngrediente(porTamanho, sizes, id, tipo)); }}
            onCreateNew={onCreateNew} getIngredientUnit={getIngredientUnit} excludeIds={[...excludeIds, ...ids]} />
        </div>
        {ids.length > 0 && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{ids.length} adicionado{ids.length !== 1 ? "s" : ""}</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            {/* Cabecalho dos tamanhos, alinhado com os campos das linhas */}
            <div className="flex items-center gap-2 px-2.5 mb-1">
              <div className="flex-1 min-w-0" />
              {sizes.map((sz) => (
                <div key={sz.id} className="w-20 flex-shrink-0 text-center text-[10px] font-bold uppercase tracking-wider text-primary truncate" title={sz.name}>{sz.name}</div>
              ))}
              <div className="w-8 flex-shrink-0" />
            </div>
            <div className="space-y-2">
              {ids.map((iid) => {
                const ing = porId[iid] || {};
                return (
                  <div key={iid} className="flex items-center gap-2 p-2.5 rounded-xl border bg-muted/20 hover:bg-muted/40 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{ing.name || "—"}</p>
                      <p className="text-[10px] text-muted-foreground tabular-nums">{getFractionText ? getFractionText(ing) : getIngredientUnit(ing)}</p>
                    </div>
                    {sizes.map((sz) => {
                      const q = qtdDe(porTamanho, sz.id, iid);
                      const custo = custoLinha(ing, Number(q) || 0);
                      return (
                        <div key={sz.id} className="w-20 flex-shrink-0 flex flex-col items-stretch gap-0.5">
                          <Input type="number" step="0.01" min={0} value={q} placeholder="Qtd"
                            aria-label={`${ing.name || iid} · ${sz.name}`}
                            onChange={(ev) => onChange(definirQtd(porTamanho, sz.id, iid, ev.target.value, tipo))}
                            className="h-8 text-sm text-center" />
                          <span className={`text-center text-[10px] font-semibold tabular-nums leading-none rounded ${custo > 0 ? e.chip : "text-muted-foreground"}`}>
                            {custo > 0 ? `R$ ${custo.toFixed(2).replace(".", ",")}` : "R$ —"}
                          </span>
                        </div>
                      );
                    })}
                    <Button type="button" onClick={() => onChange(removerIngrediente(porTamanho, iid))} variant="ghost" size="icon"
                      className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10" title="Remover">
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
